// Loads everything needed for the logged-in user and returns the in-app
// state shape that App.jsx already uses (tenants[], users{}, pets{},
// appointments[], notifications[], announcements[], reviews[], platformConfig{}).
//
// Scoping rules:
//  - superadmin → all tenants, all users (limited), all appointments, etc.
//  - owner/staff → only their tenant's data
//  - customer    → only their tenant's services/addons + their own pets/appointments

import { appFetch } from "./appFetch.js";
import { supabase } from "./supabaseClient";
import {
  fetchTenant, fetchTenantsByIds, fetchAllTenants, fetchTenantProfiles,
  fetchTenantServices, fetchTenantAddons, fetchTenantInventory,
  fetchTenantPets, fetchTenantAppointments, fetchTenantReviews,
  fetchMyNotifications, fetchAnnouncements, fetchPlatformConfig,
  fetchCustomerPets, fetchCustomerAppointments, fetchCustomerMemberships,
  fetchTenantMemberships,
  mapTenant, mapProfile, mapPet, mapAppointment, mapService, mapAddon,
  mapInventory, mapReview, mapNotification, mapAnnouncement, mapMembership,
} from "./api";

// Native owner boot uses raw fetch to bypass supabase-js's post-OAuth
// wedge. supabase-js's first batch of queries after a fresh native
// OAuth setSession reliably stalls past the 15s watchdog; raw window
// fetch on the same WebView origin completes instantly. Once boot is
// done supabase-js settles and the normal client takes over.
async function pgRest(path, accessToken, timeoutMs = 8000, _retried = false) {
  const url = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeoutMs);
  const started = Date.now();
  try {
    const resp = await appFetch(`${url}/rest/v1/${path}`, {
      signal: controller.signal,
      timeoutMs,
      headers: {
        apikey,
        Authorization: `Bearer ${accessToken || apikey}`,
        Accept: "application/json",
      },
    });
    if (!resp.ok) {
      // 401 = the access token aged out, which is ordinary: tokens last an
      // hour and boot can happen any time after that. Refresh once and retry
      // instead of failing the boot — rawWrite/rawRead have always self-healed
      // this way, but the BOOT loader never did, so an expired token dumped
      // the owner onto the login screen showing a raw
      // `pgRest 401 ... JWT expired`. Reproduced on Android on 2026-09-19,
      // one hour after signing in.
      if (resp.status === 401 && !_retried) {
        clearTimeout(tid);
        const { ensureFreshSession } = await import("./auth.js");
        const r = await ensureFreshSession().catch(() => null);
        const fresh = (r?.ok && r.session?.access_token)
          || (typeof window !== "undefined" ? window.__petosAccessToken : null);
        // Only retry with a token we did not already try; otherwise fall
        // through and report honestly rather than looping.
        if (fresh && fresh !== accessToken) return await pgRest(path, fresh, timeoutMs, true);
      }
      const text = await resp.text().catch(() => "");
      throw new Error(`pgRest ${resp.status}: ${path.slice(0,60)} ${text.slice(0, 80)}`);
    }
    const body = await resp.json();
    // eslint-disable-next-line no-console
    console.log(`[PettoBook] pgRest ${Date.now()-started}ms ${path.slice(0,50)}`);
    return body;
  } catch (e) {
    if (e?.name === "AbortError") {
      // eslint-disable-next-line no-console
      console.warn(`[PettoBook] pgRest TIMEOUT ${path.slice(0,60)}`);
      throw new Error(`pgRest timeout: ${path.slice(0,60)}`);
    }
    throw e;
  } finally {
    clearTimeout(tid);
  }
}

function indexBy(arr, key) {
  const o = {};
  for (const item of arr) o[item[key]] = item;
  return o;
}

function attachTenantChildren(tenant, profiles, services, addons, inventory) {
  if (!tenant) return null;
  // Owners count as bookable staff by default — small shops are usually
  // one-person operations and the owner IS the groomer. Add explicit staff
  // members from "員工" tab as the shop grows.
  // Honor owner-defined ordering when present; staff not in the order array
  // (newly added) fall to the end in original DB order.
  const allIds = profiles.filter(p => (p.role === "staff" || p.role === "owner") && p.tenantId === tenant.id).map(p => p.id);
  const ordered = Array.isArray(tenant.staffOrder) ? tenant.staffOrder.filter(id => allIds.includes(id)) : [];
  const rest = allIds.filter(id => !ordered.includes(id));
  tenant.staffIds = [...ordered, ...rest];
  tenant.customerIds = profiles.filter(p => p.role === "customer" && p.tenantId === tenant.id).map(p => p.id);
  tenant.services = services.filter(s => s.tenantId === tenant.id);
  tenant.addons = addons.filter(a => a.tenantId === tenant.id);
  tenant.inventory = inventory.filter(i => i.tenantId === tenant.id);
  // legacy compat
  tenant.cancelPolicy = {
    deadlineHours: tenant.cancellationPolicy?.deadlineHours || 24,
    lateCancelFee: (tenant.cancellationPolicy?.lateCancelFeePercent || 50) / 100,
    noShowFee: (tenant.cancellationPolicy?.noShowFeePercent || 100) / 100,
  };
  return tenant;
}

function attachPetsToUsers(usersIdx, pets) {
  for (const u of Object.values(usersIdx)) u.pets = [];
  for (const p of pets) {
    const owner = usersIdx[p.ownerId];
    if (owner) owner.pets.push(p.id);
  }
}

export async function loadStateForUser(profile) {
  if (!profile) throw new Error("No profile");

  // Limit initial appointment fetch to last 90 days for faster login.
  // Older history can be loaded on-demand later. Use a LOCAL-date string
  // (not toISOString) — HKT is UTC+8, so toISOString().slice(0,10) drops
  // a day for the first 8 hours after midnight, silently truncating the
  // last 24h of appointment history right after the date rolls over.
  const ninetyDaysAgo = (() => {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  })();
  const aptRange = { from: ninetyDaysAgo };

  // Always: announcements + platform config + own notifications. Native
  // path uses raw fetch to dodge the post-OAuth supabase-js wedge.
  const isCapacitorBoot = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
  const bootToken = profile._accessToken;
  // eslint-disable-next-line no-console
  console.log("[PettoBook] loadStateForUser boot path:", { isCapacitorBoot, hasToken: !!bootToken, role: profile.role });
  let announcements, platformCfg, notifications;
  if (isCapacitorBoot && bootToken) {
    const [annRows, cfgRows, notRows] = await Promise.all([
      pgRest("announcements?select=*&order=created_at.desc", bootToken).catch(() => []),
      pgRest("platform_config?select=*&limit=1", bootToken).catch(() => []),
      pgRest(`notifications?user_id=eq.${profile.id}&select=*&order=created_at.desc&limit=80`, bootToken).catch(() => []),
    ]);
    announcements = (annRows || []).map(mapAnnouncement);
    const cfg = (cfgRows || [])[0] || null;
    platformCfg = cfg ? { config: cfg.config || {}, autoNotifications: cfg.auto_notifications || [], updatedAt: cfg.updated_at } : null;
    notifications = (notRows || []).map(mapNotification);
  } else {
    [announcements, platformCfg, notifications] = await Promise.all([
      fetchAnnouncements(),
      fetchPlatformConfig(),
      fetchMyNotifications(profile.id),
    ]);
  }

  const baseState = {
    tenants: [],
    users: {},
    pets: {},
    appointments: [],
    notifications,
    announcements,
    autoNotifications: platformCfg?.autoNotifications || [],
    platformConfig: platformCfg?.config || {},
    reviews: [],
  };

  // Native owner branch is preferred for `is_super` owners — loading
  // every tenant on boot wedges supabase-js on native (and is wasteful
  // even on web for first-paint). They can switch to SuperAdmin view
  // later; that view will lazy-load other tenants on demand.
  const isSuperOnlyView = (profile.role === "superadmin") || (profile.isSuper && !profile.tenantId);
  if (isSuperOnlyView) {
    const tenants = await fetchAllTenants();
    const allRowsPromises = tenants.map(async (t) => {
      const [profiles, services, addons, inventory, pets, appts, reviews] = await Promise.all([
        fetchTenantProfiles(t.id), fetchTenantServices(t.id), fetchTenantAddons(t.id),
        fetchTenantInventory(t.id), fetchTenantPets(t.id), fetchTenantAppointments(t.id, aptRange),
        fetchTenantReviews(t.id),
      ]);
      attachTenantChildren(t, profiles, services, addons, inventory);
      return { profiles, pets, appts, reviews };
    });
    const allRows = await Promise.all(allRowsPromises);
    const allProfiles = allRows.flatMap(r => r.profiles);
    const allPets = allRows.flatMap(r => r.pets);
    const allAppts = allRows.flatMap(r => r.appts);
    const allReviews = allRows.flatMap(r => r.reviews);
    baseState.tenants = tenants;
    baseState.users = indexBy([profile, ...allProfiles.filter(p => p.id !== profile.id)], "id");
    baseState.pets = indexBy(allPets, "id");
    baseState.appointments = allAppts;
    baseState.reviews = allReviews;
    attachPetsToUsers(baseState.users, allPets);
  } else if (profile.role === "owner" || profile.role === "staff") {
    const tenantId = profile.tenantId;
    const isCapacitor = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
    const accessToken = profile._accessToken; // injected by useAppState boot path
    let tenant, profiles, services, addons, inventory, pets, appts, reviews, memberships;
    if (isCapacitor && accessToken) {
      // Raw-fetch fast path — bypass supabase-js entirely. Each call is
      // a simple PostgREST GET so we can map server rows ourselves via
      // the same map* helpers used by the supabase-js path.
      const [tRow, pRows, svcRows, adRows, invRows, petRows, aptRows, revRows, memRows] = await Promise.all([
        pgRest(`tenants?id=eq.${tenantId}&select=*`, accessToken),
        pgRest(`profiles?tenant_id=eq.${tenantId}&select=*`, accessToken),
        pgRest(`services?tenant_id=eq.${tenantId}&select=*`, accessToken),
        pgRest(`addons?tenant_id=eq.${tenantId}&select=*`, accessToken),
        pgRest(`inventory?tenant_id=eq.${tenantId}&select=*`, accessToken),
        pgRest(`pets?tenant_id=eq.${tenantId}&select=*`, accessToken),
        pgRest(`appointments?tenant_id=eq.${tenantId}&date=gte.${ninetyDaysAgo}&deleted_at=is.null&select=*`, accessToken),
        pgRest(`reviews?tenant_id=eq.${tenantId}&select=*`, accessToken),
        pgRest(`customer_memberships?tenant_id=eq.${tenantId}&select=*`, accessToken),
      ]);
      tenant = (tRow || []).map(mapTenant)[0];
      profiles = (pRows || []).map(mapProfile);
      services = (svcRows || []).map(mapService);
      addons = (adRows || []).map(mapAddon);
      inventory = (invRows || []).map(mapInventory);
      pets = (petRows || []).map(mapPet);
      appts = (aptRows || []).map(mapAppointment);
      reviews = (revRows || []).map(mapReview);
      memberships = (memRows || []).map(mapMembership);
    } else {
      [tenant, profiles, services, addons, inventory, pets, appts, reviews, memberships] = await Promise.all([
        fetchTenant(tenantId), fetchTenantProfiles(tenantId), fetchTenantServices(tenantId),
        fetchTenantAddons(tenantId), fetchTenantInventory(tenantId), fetchTenantPets(tenantId),
        fetchTenantAppointments(tenantId, aptRange), fetchTenantReviews(tenantId),
        fetchTenantMemberships(tenantId).catch(() => []),
      ]);
    }
    attachTenantChildren(tenant, profiles, services, addons, inventory);
    baseState.tenants = [tenant].filter(Boolean);
    // ensure current profile is in users
    const allProfiles = profiles.some(p => p.id === profile.id) ? profiles : [...profiles, profile];
    baseState.users = indexBy(allProfiles, "id");
    baseState.pets = indexBy(pets, "id");
    baseState.appointments = appts;
    baseState.reviews = reviews;
    // Per-customer loyalty/stamp/wallet rows for this shop. Powers the CRM
    // stamp-card, wallet and points views (which read state.memberships).
    baseState.memberships = memberships || [];
    attachPetsToUsers(baseState.users, pets);
  } else if (profile.role === "customer") {
    // Multi-shop aware: a Google-authenticated customer can be a member of
    // multiple shops via customer_memberships. Legacy phone-based customers
    // have profile.tenantId set and no memberships row (the backfill created
    // one mirror, but we keep the legacy fallback to be safe).
    const memberships = await fetchCustomerMemberships(profile.id).catch(() => []);
    const legacyTenantId = profile.tenantId || null;

    // Determine the active tenant: explicit window slug from PublicBooking /
    // OAuth-return takes priority, then legacy pin, then most-recent join.
    let activeTenantId = null;
    if (typeof window !== "undefined" && window.__petosActiveCustomerSlug) {
      const slug = window.__petosActiveCustomerSlug;
      // Resolve slug→id by fetching candidate tenants the user is a member of
      // We can't lookup by slug without fetching, so do it inline below.
      activeTenantId = null; // resolved after we have tenant rows
      // Stash for resolution below
      var __wantedSlug = slug;
    }

    // Effective membership set (synthesize a fallback for legacy customers).
    const effectiveMemberships = memberships.length
      ? memberships
      : (legacyTenantId
          ? [{ userId: profile.id, tenantId: legacyTenantId, displayName: profile.name, phone: profile.phone, wallet: profile.wallet || 0, points: profile.points || 0, walletTx: profile.walletTx || [], tags: profile.tags || [], joinedAt: null }]
          : []);

    if (effectiveMemberships.length === 0) {
      // Authenticated but unaffiliated — show empty state. App.jsx will route
      // by role; CustomerApp without tenants renders a "請使用店家連結" hint.
      baseState.users = indexBy([profile], "id");
      return baseState;
    }

    // Fetch every shop the user is a member of in a single round-trip.
    // Was N separate fetchTenant calls (N+1 query); now one IN clause.
    let tenantsArr = [];
    try {
      tenantsArr = await fetchTenantsByIds(effectiveMemberships.map(m => m.tenantId));
    } catch {
      // Fall back to per-id fetch so a single bad row doesn't blank the
      // customer's shop list. Logs swallowed because partial set still
      // renders the picker.
      tenantsArr = (await Promise.all(
        effectiveMemberships.map(m => fetchTenant(m.tenantId).catch(() => null))
      )).filter(Boolean);
    }

    // Resolve active tenant id now that we have slugs.
    if (typeof window !== "undefined" && window.__petosActiveCustomerSlug) {
      const want = window.__petosActiveCustomerSlug;
      const match = tenantsArr.find(t => t.bookingSlug === want);
      if (match) activeTenantId = match.id;
    }
    if (!activeTenantId) activeTenantId = legacyTenantId || effectiveMemberships[0].tenantId;
    const activeTenant = tenantsArr.find(t => t.id === activeTenantId) || tenantsArr[0];
    if (activeTenant) activeTenantId = activeTenant.id;

    // For the active tenant only, load services + addons + staff (needed for
    // the booking UI). Other-shop tenants get their basic info so the shop
    // switcher can render names; switching active triggers a fresh reload.
    // Customers can't SELECT staff profile rows directly (RLS scoped to
     // owner + self). Use the SECURITY DEFINER RPC that returns only the
     // non-sensitive booking-display fields (id/name/role/enabled/avatar)
     // so the groomer dropdown can render.
    const [services, addons, staffRows, ownProfile, pets, appts] = await Promise.all([
      fetchTenantServices(activeTenantId),
      fetchTenantAddons(activeTenantId),
      supabase.rpc("list_tenant_staff_public", { p_tenant_id: activeTenantId }).then(r => r.data || []),
      fetchTenantProfiles(activeTenantId).then(ps => ps.filter(p => p.id === profile.id)),
      fetchCustomerPets(profile.id),
      fetchCustomerAppointments(profile.id, aptRange),
    ]);
    const staffProfiles = staffRows.map(r => ({
      id: r.id, name: r.name, role: r.role, enabled: r.enabled !== false,
      tenantId: activeTenantId, avatar: r.avatar || null, pets: [],
      joinDate: r.join_date || null, leaveDate: r.leave_date || null,
      jobType: r.job_type || null,
      staffHours: (r.staff_hours && typeof r.staff_hours === "object") ? r.staff_hours : {},
      timeOff: Array.isArray(r.time_off) ? r.time_off : [],
    }));

    // Attach services / addons / staff to the active tenant. Other tenants
    // get empty arrays — switching active reloads the state.
    for (const t of tenantsArr) {
      if (t.id === activeTenantId) {
        attachTenantChildren(t, staffProfiles, services, addons, []);
      } else {
        t.staffIds = []; t.customerIds = []; t.services = []; t.addons = []; t.inventory = [];
        t.cancelPolicy = {
          deadlineHours: t.cancellationPolicy?.deadlineHours || 24,
          lateCancelFee: (t.cancellationPolicy?.lateCancelFeePercent || 50) / 100,
          noShowFee: (t.cancellationPolicy?.noShowFeePercent || 100) / 100,
        };
      }
    }

    baseState.tenants = tenantsArr;
    // Merge active-tenant membership state INTO the user record so legacy
    // UI that reads liveUser.wallet / .points keeps working.
    const activeMem = effectiveMemberships.find(m => m.tenantId === activeTenantId);
    const augmentedProfile = activeMem
      ? { ...profile, tenantId: activeTenantId, wallet: activeMem.wallet, points: activeMem.points, walletTx: activeMem.walletTx, tags: activeMem.tags, phone: activeMem.phone || profile.phone, name: activeMem.displayName || profile.name }
      : { ...profile, tenantId: activeTenantId };
    baseState.users = indexBy([augmentedProfile, ...staffProfiles.filter(p => p.id !== profile.id)], "id");
    baseState.pets = indexBy(pets, "id");
    baseState.appointments = appts;
    baseState.memberships = effectiveMemberships;
    baseState.activeCustomerTenantId = activeTenantId;
    attachPetsToUsers(baseState.users, pets);
  }

  return baseState;
}

// Subscribe to all changes relevant to this user. Returns a function that
// unsubscribes — also exposes `.reconnect()` (tear down + resubscribe, used by
// the visibility-change handler) and `.ping()` (heartbeat probe; returns a
// promise that resolves on pong or rejects on timeout, so callers can detect
// zombie connections).
export function subscribeChanges({ profile, onChange }) {
  if (!profile) {
    const noop = () => {};
    noop.reconnect = () => {};
    noop.ping = () => Promise.resolve(true);
    noop.pingAuthed = () => Promise.resolve(true);
    return noop;
  }
  const tenantFilter = profile.tenantId ? `tenant_id=eq.${profile.tenantId}` : undefined;
  let channels = [];

  const make = (name, filter) => {
    const ch = supabase.channel("petos:" + name + ":" + profile.id + ":" + Date.now())
      .on("postgres_changes", { event: "*", schema: "public", table: name, filter }, (payload) => {
        try { onChange({ table: name, payload }); } catch (e) { /* eslint-disable-next-line no-console */ console.error(e); }
      })
      .subscribe();
    channels.push(ch);
  };

  const subscribe = () => {
    if (profile.role === "superadmin") {
      ["tenants","profiles","pets","services","addons","inventory","appointments","reviews","notifications","announcements","customer_memberships","customer_packages"]
        .forEach(t => make(t));
    } else if (tenantFilter) {
      ["tenants","profiles","pets","services","addons","inventory","appointments","reviews"]
        .forEach(t => make(t, t === "tenants" ? `id=eq.${profile.tenantId}` : tenantFilter));
      // customer_memberships + customer_packages: customer sees their
      // OWN rows across all tenants; staff/owner sees ALL rows for their
      // tenant. Without these, owner-side topups / package grants did
      // not appear on the customer's open tab until manual reload.
      const role = profile.role;
      if (role === "customer") {
        make("customer_memberships", `user_id=eq.${profile.id}`);
        make("customer_packages",    `customer_id=eq.${profile.id}`);
      } else {
        make("customer_memberships", tenantFilter);
        make("customer_packages",    tenantFilter);
      }
      make("notifications", `user_id=eq.${profile.id}`);
    } else {
      make("customer_memberships", `user_id=eq.${profile.id}`);
      make("customer_packages",    `customer_id=eq.${profile.id}`);
      make("notifications",        `user_id=eq.${profile.id}`);
    }
  };

  const teardown = () => {
    channels.forEach(c => { try { supabase.removeChannel(c); } catch { /* ignore */ } });
    channels = [];
  };

  subscribe();

  // ANDROID: swapping channels is not enough — the SOCKET must be replaced.
  // After the app idles, its WebSocket is a zombie: readyState still OPEN, but
  // nothing flows. New channels call socket.connect(), which is a no-op while
  // isConnected() is true, so they joined the same dead socket and live
  // updates never came back (edge logs: no new websocket 101 after an idle
  // reopen, while REST calls on the same reopen succeeded). disconnect() forces
  // phoenix to drop it — its teardown gives up waiting after ~1.5s and nulls
  // the conn — so the next subscribe opens a fresh socket. Old channels are
  // removed AFTER the disconnect: with no socket to push to, their leave
  // settles at once instead of waiting 10s on the zombie (and they can't be
  // rejoined as duplicates when the new socket opens).
  // Android only: iOS/web keep the plain swap — an earlier reconnect change
  // coincided with iPad resume wedges, and they are not the reported problem.
  const isAndroid = () => {
    try { return !!(window.Capacitor?.isNativePlatform?.() && window.Capacitor.getPlatform?.() === "android"); } catch { return false; }
  };
  let closed = false, reconnectSeq = 0;
  const unsub = () => { closed = true; reconnectSeq++; teardown(); };
  unsub.reconnect = () => {
    if (!isAndroid()) { teardown(); subscribe(); return; }
    const seq = ++reconnectSeq;
    const old = channels;
    channels = [];
    (async () => {
      try { await Promise.race([supabase.realtime.disconnect(), new Promise((r) => setTimeout(r, 4000))]); } catch { /* best effort */ }
      old.forEach(c => { try { supabase.removeChannel(c); } catch { /* ignore */ } });
      if (closed || seq !== reconnectSeq) return;   // unsubscribed or superseded meanwhile
      subscribe();
      // eslint-disable-next-line no-console
      console.warn("[PettoBook realtime] Android: socket replaced, " + channels.length + " channels resubscribed");
    })();
  };
  // Heartbeat: hit a trivial RPC and time it out at 5s. If it rejects, the
  // socket layer or the network is dead and callers should reconnect.
  unsub.ping = (userId) => {
    // NATIVE: ask the hardened raw fetch. supabase-js `.from()` never resolves
    // on Android — measured: every ping timed out at 5s while raw queries on
    // the same connection answered in ~600ms — so this probe reported "dead"
    // on every tick and had the heartbeat reconnecting realtime and reloading
    // all state, forever. See pingRest() in api.js.
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
      return import("./api.js").then(api => api.pingRest(userId)).catch(() => false);
    }
    return new Promise((resolve) => {
      const t = setTimeout(() => resolve(false), 5000);
      // Cheap query — counts available extensions (no RLS, public). Any 2xx
      // response means the path is alive.
      supabase.from("platform_config").select("id", { count: "exact", head: true }).limit(1)
        .then(() => { clearTimeout(t); resolve(true); })
        .catch(() => { clearTimeout(t); resolve(false); });
    });
  };
  // AUTHENTICATED liveness probe (WEB resume/heartbeat use this). Unlike ping()
  // — which hits the PUBLIC platform_config and stays green even when the login
  // token or the authenticated socket is wedged (the "reads work but can't
  // save" false-alive) — this exercises the SAME authed + RLS path saves use.
  // Resolves true on ANY response (incl. a clean 401: the connection is alive,
  // and a stale token is handled separately by ensureFreshSession), and false
  // ONLY on a HANG (the wedge symptom) or a network throw → so a genuinely
  // wedged authenticated connection is detected and recovered by a reload.
  unsub.pingAuthed = (userId) => new Promise((resolve) => {
    if (!userId) return resolve(true);
    const t = setTimeout(() => resolve(false), 5000);
    supabase.from("profiles").select("id").eq("id", userId).limit(1)
      .then(() => { clearTimeout(t); resolve(true); })
      .catch(() => { clearTimeout(t); resolve(false); });
  });
  return unsub;
}
